import React from 'react';
import PropTypes from 'prop-types';

import stringUtils from '../../../../qti/utils/StringUtils';

import KeyboardHelpAndPrompt from '../../keyboardHelp/KeyboardHelpAndPrompt';
import GapChoicesContainer from './GapChoicesContainer';

import EventTracker from './EventTracker';
import LoadTracker from '../../../../services/LoadTracker';
import ImageDisplay from '../../media/image/ImageDisplay';
import AssociableHotspot from '../graphicGapMatch/AssociableHotspot';

import styles from './graphicGapMatchInteraction.scss';
import sessionReader from '../../../../store/readHelpers/SessionReader';
import PaperBasedInteractionInput from '../paperBasedInteraction/PaperBasedInteractionInput';

class GraphicGapMatchInteraction extends React.Component {
    static propTypes = {
        callback: PropTypes.func.isRequired, // callback(responseIdentifier, [simpleChoice identifiers])
        correctResponses: PropTypes.array,
        handleInteractionReady: PropTypes.func.isRequired,
        model: PropTypes.object.isRequired, // model taken from responseModels related to this interaction
        userResponse: PropTypes.array.isRequired, // array with simpleChoice identifiers
        services: PropTypes.object,
        settingValues: PropTypes.object,
        isReadOnly: PropTypes.bool,
        questionModel: PropTypes.object,
        direction: PropTypes.string,
    };

    constructor(props) {
        super(props);
        this.state = {
            uid: stringUtils.getUID(),
            reCalculatedGapChoiceDimensions: undefined,
        };
        this.maps = this.getMaps(this.props);
        this.eventTracker = new EventTracker();
        this.setReadyTracker();
    }

    componentDidMount() {
        this.reportMounted(true);
    }

    // eslint-disable-next-line camelcase,react/sort-comp
    UNSAFE_componentWillReceiveProps(newProps) {
        this.maps = this.getMaps(newProps);
    }

    componentWillUnmount() {
        this.readyTracker.kill();
    }

    setReadyTracker() {
        const { adjustGapChoicesToBeTheSameSize, hotspotModels, identifier } = this.props.model;
        this.readyTracker = new LoadTracker(this.props.handleInteractionReady, `GraphicGapMatchInteraction ${identifier} > Gaps and Gap Choices`);
        this.reportMounted = this.readyTracker.addItemToTrack(`GraphicGapMatchInteraction ${identifier} > Mounted`);
        this.reportGapChoicesContainerReady = this.readyTracker.addItemToTrack(`GraphicGapMatchInteraction ${identifier} > GapTextsContainer`);
        this.hotspotTrackers = hotspotModels
            .reduce((trackers, hotspotModel) => {
                trackers[hotspotModel.identifier] = this.readyTracker.addItemToTrack(`AssociableHotspot ${hotspotModel.identifier}`);
                return trackers;
            }, {});
        if (this.shouldShowBackgroundImage()) {
            this.reportBackgroundImageReady = this.readyTracker.addItemToTrack(`GraphicGapMatchInteraction ${identifier} > BG Image`);
        }
        if (adjustGapChoicesToBeTheSameSize) {
            this.sizeAdjustmentReporter = this.readyTracker.addItemToTrack('GapChoicesContainer > sizeAdjustment');
        }
        this.readyTracker.setAllItemsAdded();
    }

    getMaps = (props) => {
        const { userResponse, correctResponses } = props;
        const maps = {
            droppedGapChoices: [],
            gapChoicesInHotspot: {}, // Key = gapChoice.identifier, value = hotspot.identifier
        };

        maps.droppedGapChoices = userResponse
            .map(item => item.split(' ')[0])
            .filter((item, index, array) => array.indexOf(item) === index); // Get only unique identifiers

        let responses = userResponse;
        if (!userResponse?.length && correctResponses?.length) {
            responses = correctResponses;
        }
        responses
            .forEach((item) => {
                const [gapChoiceIdentifier, hotspotIdentifier] = item.split(' ');
                if (maps.gapChoicesInHotspot[hotspotIdentifier]) {
                    maps.gapChoicesInHotspot[hotspotIdentifier].push(gapChoiceIdentifier);
                } else {
                    maps.gapChoicesInHotspot[hotspotIdentifier] = [gapChoiceIdentifier];
                }
            });

        return maps;
    };

    isGapChoiceInHotspot = (gapChoiceIdentifier, hotspotIdentifier) => {
        const { gapChoicesInHotspot } = this.maps;
        if (gapChoicesInHotspot[hotspotIdentifier] === undefined) {
            return false;
        }
        return gapChoicesInHotspot[hotspotIdentifier].indexOf(gapChoiceIdentifier) !== -1;
    };

    isHotspotFilled = hotspotIdentifier => (this.maps.gapChoicesInHotspot[hotspotIdentifier] || []).length > 0;

    getNextAvailableHotspotIdentifier = (direction, gapChoiceIdentifier, currentHotspotIdentifier) => {
        const { hotspotModels } = this.props.model;
        const goingUp = direction === 'up';
        const hotspotList = goingUp ? hotspotModels.slice(0).reverse() : hotspotModels;
        const currentHotspotIndex = hotspotList
            .map(hotspotModel => hotspotModel.identifier)
            .indexOf(currentHotspotIdentifier);

        const nextHotspot = hotspotList
            .slice(currentHotspotIndex + 1)
            .find((hotspotModel) => {
                if (this.isGapChoiceInHotspot(gapChoiceIdentifier, hotspotModel.identifier)
                    || (hotspotModel.matchMax === '1' && this.isHotspotFilled(hotspotModel.identifier))) {
                    return false;
                }

                return true;
            });

        return nextHotspot ? nextHotspot.identifier : undefined;
    };

    /**
     * Returns a new user response array, with any matches to hotspotIdentifierToClear filtered out.
     * @param {Array} userResponse
     * @param {string} hotspotIdentifierToClear
     * @return {Array}
     */
    removeMatchesWithHotspotIdentifier = (userResponse, hotspotIdentifierToClear) => {
        return userResponse.filter(match => match.split(' ')[1] !== hotspotIdentifierToClear);
    };

    removeMatchFromResponse = (userResponse, matchToRemove) => userResponse.filter(match => match !== matchToRemove);

    handleReCalculatedGapChoiceDimensions = (dimensions) => {
        this.setState(
            { reCalculatedGapChoiceDimensions: dimensions },
            () => this.sizeAdjustmentReporter(true),
        );
    };

    handleGapChoicesContainerReady = success => this.reportGapChoicesContainerReady(success);

    handleUserResponse = (gapChoiceIdentifier, selectedHotspotIdentifier, prevHotspotIdentifier) => {
        const { callback, model, userResponse } = this.props;
        let newResponse = userResponse.slice();

        if (prevHotspotIdentifier && selectedHotspotIdentifier !== prevHotspotIdentifier) {
            newResponse = this.removeMatchFromResponse(userResponse, `${gapChoiceIdentifier} ${prevHotspotIdentifier}`);
        }

        const match = `${gapChoiceIdentifier} ${selectedHotspotIdentifier}`;
        if (selectedHotspotIdentifier && newResponse.indexOf(match) === -1) {
            const hotspotModel = model.hotspotModels.find(item => item.identifier === selectedHotspotIdentifier);
            const canAppend = hotspotModel && hotspotModel.matchMax === '0';
            if (!canAppend) {
                newResponse = this.removeMatchesWithHotspotIdentifier(newResponse, selectedHotspotIdentifier);
            }
            newResponse.push(match);
        }

        callback(model.identifier, newResponse);
    };

    removeGapChoiceFromHotspot = (gapChoiceIdentifier, hotspotIdentifier) => {
        this.handleUserResponse(gapChoiceIdentifier, undefined, hotspotIdentifier);
    };

    handleGapChoiceArrowKeyUp = (gapChoiceIdentifier, hotspotIdentifier, direction) => {
        const nextHotspotIdentifier = this.getNextAvailableHotspotIdentifier(direction, gapChoiceIdentifier, hotspotIdentifier);
        this.handleUserResponse(gapChoiceIdentifier, nextHotspotIdentifier, hotspotIdentifier);
    };

    handleGapChoiceDeleteKeyUp = (gapChoiceIdentifier, hotspotIdentifier) => {
        this.removeGapChoiceFromHotspot(gapChoiceIdentifier, hotspotIdentifier);
    };

    handleGapChoiceDragStart = () => this.markValidTargets();

    handleGapChoiceDragEnd = (gapChoiceIdentifier, hotspotIdentifier, droppedOnSelf) => {
        if (hotspotIdentifier && !droppedOnSelf) {
            this.removeGapChoiceFromHotspot(gapChoiceIdentifier, hotspotIdentifier);
        }

        this.unMarkTargets();
    };

    handleDrop = (gapChoiceIdentifier, nextHotspotIdentifier, previousHotspotIdentifier) => {
        if (gapChoiceIdentifier) {
            this.handleUserResponse(gapChoiceIdentifier, nextHotspotIdentifier, previousHotspotIdentifier);
        }
    };

    markValidTargets = () => {
        Array
            .from(this.rootElement.getElementsByClassName('hotspot'))
            .forEach(hotspot => hotspot.classList.add('valid-drop-target'));
    };

    unMarkTargets = () => {
        Array
            .from(this.rootElement.getElementsByClassName('hotspot'))
            .forEach(hotspot => hotspot.classList.remove('valid-drop-target'));
    };

    shouldShowBackgroundImage = () => !!this.getBackgroundImageModel().url;

    getBackgroundImageModel() {
        const { model } = this.props;
        const { backgroundImageModel = { width: 620, height: model.canvasHeight || 400 } } = model;
        return backgroundImageModel;
    }

    getAssociableHotspotElements() {
        const { model, questionModel, services } = this.props;
        const { reCalculatedGapChoiceDimensions } = this.state;
        const { gapChoicesInHotspot, droppedGapChoices } = this.maps;

        return model.hotspotModels.map((hotspotModel) => {
            const gapChoiceIdentifiers = gapChoicesInHotspot[hotspotModel.identifier] || [];
            const gapChoiceModels = model.gapChoiceModels.filter(gapChoiceModel => gapChoiceIdentifiers.indexOf(gapChoiceModel.identifier) > -1);
            const gapNumber = services.scorableItemNumberingService.getInteractionChoiceNumber(
                questionModel.contentRevisionId, model.identifier, hotspotModel.identifier
            );
            const isActive = services.scorableItemNumberingService.isActiveScorableItem(gapNumber);
            return (
                <AssociableHotspot
                    {...this.props}
                    correctResponses={this.props.correctResponses}
                    eventTracker={this.eventTracker}
                    droppedGapChoices={droppedGapChoices}
                    gapChoiceModels={gapChoiceModels}
                    handleDrop={this.handleDrop}
                    handleUserResponse={this.handleUserResponse}
                    handleGapChoiceArrowKeyUp={this.handleGapChoiceArrowKeyUp}
                    handleGapChoiceDeleteKeyUp={this.handleGapChoiceDeleteKeyUp}
                    handleGapChoiceDragStart={this.handleGapChoiceDragStart}
                    handleGapChoiceDragEnd={this.handleGapChoiceDragEnd}
                    hotspotModel={hotspotModel}
                    isReadOnly={this.props.isReadOnly}
                    mediaTracker={this.readyTracker}
                    reCalculatedGapChoiceDimensions={reCalculatedGapChoiceDimensions}
                    reportReady={this.hotspotTrackers[hotspotModel.identifier]}
                    services={this.props.services}
                    userResponse={this.props.userResponse}
                    key={hotspotModel.identifier}
                    orderNumber={gapNumber}
                    isActive={isActive}
                />
            );
        });
    }

    getPaperBasedHP() {
        const { model, questionModel, services } = this.props;

        return model.hotspotModels.map((hotspot) => {
            const gapNumber = services.scorableItemNumberingService.getInteractionChoiceNumber(
                questionModel.contentRevisionId, model.identifier, hotspot.identifier
            );

            return (
                <PaperBasedInteractionInput key={hotspot.identifier} position={hotspot.position} orderNumber={gapNumber} isShortSize />
            );
        });
    }

    render() {
        const { model, direction, services = {}, settingValues = {} } = this.props;
        const backgroundImageModel = this.getBackgroundImageModel();
        let tokenPosition = model.tokenPosition;
        if (sessionReader.isPrintStylePaperBased() || !tokenPosition) {
            tokenPosition = 'top';
        }
        const canvasStyle = {
            height: `${backgroundImageModel.height}px`,
            width: `${backgroundImageModel.width}px`,
        };

        return (
            <div
                className="interaction skipMathCache allowed-break-inside"
                id={this.state.uid}
                ref={(element) => { this.rootElement = element; }}
                dir={direction}
            >
                <KeyboardHelpAndPrompt
                    captionKey="interactions.gapMatch.keyboardInteractionHelpText"
                    translationService={services.translationService}
                    model={model}
                    showAccessibilityHelpers={settingValues.show_accessibility_helpers}
                />
                <div className={`${styles.wrapper} tokens-${tokenPosition}`}>
                    <GapChoicesContainer
                        adjustGapChoicesToBeTheSameSize={this.props.model.adjustGapChoicesToBeTheSameSize}
                        eventTracker={this.eventTracker}
                        droppedGapChoices={this.maps.droppedGapChoices}
                        gapChoiceModels={model.gapChoiceModels}
                        handleUserResponse={this.handleUserResponse}
                        handleGapChoiceDeleteKeyUp={this.handleGapChoiceDeleteKeyUp}
                        handleGapChoiceArrowKeyUp={this.handleGapChoiceArrowKeyUp}
                        handleGapChoiceDragStart={this.handleGapChoiceDragStart}
                        handleGapChoiceDragEnd={this.handleGapChoiceDragEnd}
                        isReadOnly={this.props.isReadOnly}
                        reCalculatedGapChoiceDimensions={this.state.reCalculatedGapChoiceDimensions}
                        reportReCalculatedGapChoiceDimensions={this.handleReCalculatedGapChoiceDimensions}
                        reportReady={this.handleGapChoicesContainerReady}
                        reuseAlternatives={model.reuseAlternatives}
                        services={services}
                    />
                    <div className={styles.canvas}>
                        {
                            this.shouldShowBackgroundImage() ?
                                <div
                                    className={`${styles.backgroundImageWrapper} background-image-wrapper`}
                                    style={canvasStyle}
                                >
                                    <ImageDisplay
                                        model={backgroundImageModel}
                                        reportReady={this.reportBackgroundImageReady}
                                        autoScale
                                    />
                                </div> : null
                        }
                        {
                            sessionReader.isPrintStylePaperBased()
                                ? this.getPaperBasedHP()
                                : this.getAssociableHotspotElements()
                        }
                    </div>
                </div>
            </div>
        );
    }
}

export default GraphicGapMatchInteraction;
